import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STAGES, LOAN_TYPES, formatINR, formatDate, inputCls, labelCls, btnPrimary, btnSecondary } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileUp, Trash2, ArrowRight, Clock, Plus, Eye, FileText, ScrollText } from "lucide-react";

const DOC_TYPES = ["Aadhaar", "PAN", "Salary Slip", "Bank Statement", "GST Return", "Property Document", "Other"];

function PartsEditor({ parts, setParts, uploadingIdx, onProofPick }) {
  const setRow = (i, key, val) => { const n = [...parts]; n[i] = { ...n[i], [key]: val }; setParts(n); };
  const addRow = () => setParts([...parts, { no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
  const removeRow = (i) => setParts(parts.length > 1 ? parts.filter((_, x) => x !== i) : [{ no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
  const total = parts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  return (
    <div className="sm:col-span-2 space-y-3" data-testid="parts-editor">
      {parts.map((p, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`part-row-${i}`}>
          <div className="sm:col-span-4">
            <label className={labelCls}>Part {i + 1} · Disb No *</label>
            <input data-testid={`part-no-input-${i}`} value={p.no} onChange={(e) => setRow(i, "no", e.target.value)} className={inputCls} placeholder="Reference no" />
          </div>
          <div className="sm:col-span-3">
            <label className={labelCls}>Amount (₹) *</label>
            <input data-testid={`part-amount-input-${i}`} type="number" value={p.amount} onChange={(e) => setRow(i, "amount", e.target.value)} className={inputCls} placeholder="0" />
          </div>
          <div className="sm:col-span-4">
            <label className={labelCls}>Proof *</label>
            <label className={`${btnSecondary} cursor-pointer w-full justify-center`} data-testid={`part-proof-label-${i}`}>
              <FileUp size={15} /> {uploadingIdx === i ? "Uploading…" : (p.proof_name ? "Replace" : "Upload")}
              <input type="file" className="hidden" data-testid={`part-proof-input-${i}`} onChange={(e) => onProofPick(i, e, parts, setParts)} />
            </label>
            {p.proof_name && <div className="text-xs text-emerald-600 mt-1 truncate" data-testid={`part-proof-name-${i}`}>{p.proof_name}</div>}
          </div>
          <div className="sm:col-span-1 flex justify-end">
            <button type="button" data-testid={`part-remove-${i}`} onClick={() => removeRow(i)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button type="button" data-testid="add-part-row" onClick={addRow} className={btnSecondary}><Plus size={15} /> Add Part</button>
        <div className="text-sm text-slate-600">Total: <span className="font-mono font-semibold text-slate-900" data-testid="parts-total">{formatINR(total)}</span></div>
      </div>
    </div>
  );
}

function ContributionTab({ lead, contribution, setContribution }) {
  useEffect(() => {
    api.get(`/leads/${lead.id}/contribution`).then((r) => setContribution(r.data)).catch(() => setContribution(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, lead.status, lead.disbursed_amount, lead.sanction_amount]);
  if (!contribution) return <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>;
  const c = contribution;
  const projected = c.basis === "projected";
  const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-4" data-testid="contribution-tab">
      <div className={`rounded-xl border p-4 ${projected ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{projected ? "Projected Contribution" : "Actual Contribution"}</div>
            <div className="text-xs text-slate-600 mt-0.5">{projected ? `Based on sanction / requested amount — final numbers will update after disbursement.` : `Locked from disbursement + booked commission.`}</div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-md font-semibold ${projected ? "bg-amber-200 text-amber-900" : "bg-emerald-200 text-emerald-900"}`}>{projected ? "PROJECTED" : "ACTUAL"}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{projected ? "Sanction / Requested Amount" : "Disbursed Amount"}</div><div className="text-lg font-semibold text-slate-800 mt-1" data-testid="contrib-basis-amount">{inr(c.basis_amount)}</div></div>
        <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">Lender</div><div className="text-lg font-semibold text-slate-800 mt-1">{c.lender_name || "—"}</div></div>
        <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">Lender Payout</div><div className="text-lg font-semibold text-emerald-700 mt-1" data-testid="contrib-payout">{inr(c.lender_payout)}</div></div>
        <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">DSA Commission</div><div className="text-lg font-semibold text-rose-600 mt-1" data-testid="contrib-dsa">− {inr(c.dsa_commission)}</div></div>
      </div>
      <div className={`rounded-xl border-2 p-5 ${c.contribution >= 0 ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`} data-testid="contrib-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contribution</div>
            <div className={`text-3xl font-extrabold mt-1 ${c.contribution >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{inr(c.contribution)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Margin</div>
            <div className={`text-2xl font-bold mt-1 ${c.margin_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{c.margin_pct}%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LenderAttemptsTab({ lead, viewProof, formatDate }) {
  const currentAttempt = {
    lender_id: lead.lender_id, lender_name: lead.lender_name || "—",
    bank_application_no: lead.bank_application_no || "—",
    sanction_amount: lead.sanction_amount || 0, irr: lead.irr || 0, tenure: lead.tenure || 0,
    disbursement_no: lead.disbursement_no || "", disbursed_amount: lead.disbursed_amount || 0,
    disbursement_parts: lead.disbursement_parts || [],
    pdd: lead.pdd, pdd_history: lead.pdd_history || [],
    undertaking: lead.undertaking, status_at_reassign: lead.status,
    attempt_no: (lead.lender_attempts || []).length + 1, is_current: true,
  };
  const past = (lead.lender_attempts || []).slice().reverse();
  const all = [currentAttempt, ...past];
  const [idx, setIdx] = useState(0);
  const a = all[idx];
  return (
    <div className="space-y-4" data-testid="lender-attempts-tab">
      <div className="flex gap-2 flex-wrap">
        {all.map((x, i) => (
          <button
            key={i}
            data-testid={`lender-attempt-tab-${i}`}
            onClick={() => setIdx(i)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${idx === i ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
            {x.is_current ? `Current · ${x.lender_name}` : `Lender ${x.attempt_no} · ${x.lender_name}`}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4" data-testid={`lender-attempt-view-${idx}`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Lender</div><div className="font-semibold text-slate-800">{a.lender_name}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Bank App No</div><div className="text-slate-700">{a.bank_application_no || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{a.is_current ? "Current Status" : "Status at Reassign"}</div><div className="text-slate-700">{a.status_at_reassign || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Sanction Amount</div><div className="text-slate-700">₹{Number(a.sanction_amount || 0).toLocaleString("en-IN")}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">IRR / Tenure</div><div className="text-slate-700">{a.irr || 0}% · {a.tenure || 0} months</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Disbursed</div><div className="text-slate-700">{a.disbursement_no || "—"} · ₹{Number(a.disbursed_amount || 0).toLocaleString("en-IN")}</div></div>
        </div>
        {(a.disbursement_parts || []).length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Disbursement Parts</div>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500"><tr><th className="text-left px-3 py-1.5">No</th><th className="text-left px-3 py-1.5">Amount</th><th className="text-left px-3 py-1.5">Proof</th></tr></thead>
                <tbody>{a.disbursement_parts.map((p, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5 font-mono">{p.no}</td><td className="px-3 py-1.5">₹{Number(p.amount || 0).toLocaleString("en-IN")}</td><td className="px-3 py-1.5">{p.proof_doc_id ? <button onClick={() => viewProof(p.proof_doc_id)} className="text-emerald-600 hover:underline inline-flex items-center gap-1"><Eye size={12} /> {p.proof_name || "View"}</button> : "—"}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        {a.pdd && (
          <div className={`rounded-lg border p-3 ${a.pdd.ops_decision === "approved" ? "border-emerald-200 bg-emerald-50" : a.pdd.ops_decision === "rejected" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">PDD — {a.pdd.loan_type} {a.pdd.ops_decision ? `· ${a.pdd.ops_decision.toUpperCase()}` : ""}</div>
            <div className="text-xs text-slate-600 mb-2">Submitted by {a.pdd.submitted_by || "—"}{a.pdd.submitted_at ? ` · ${formatDate(a.pdd.submitted_at)}` : ""}</div>
            <table className="w-full text-xs bg-white rounded overflow-hidden">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500"><tr><th className="text-left px-2 py-1">Activity</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">Remark</th><th className="text-left px-2 py-1">Doc</th></tr></thead>
              <tbody>{(a.pdd.activities || []).map((x, i) => (
                <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1 font-medium">{x.name}</td><td className="px-2 py-1">{x.deviation ? "Deviation" : "Completed"}</td><td className="px-2 py-1 text-slate-600">{x.remark || "—"}</td><td className="px-2 py-1">{x.document_id ? <button onClick={() => viewProof(x.document_id)} className="text-emerald-600 hover:underline inline-flex items-center gap-1"><Eye size={12} /> View</button> : "—"}</td></tr>
              ))}</tbody>
            </table>
            {a.pdd.ops_decision && <div className="text-xs text-slate-700 mt-2"><b>Ops Remark:</b> {a.pdd.ops_note || "—"} <span className="text-slate-400">· {a.pdd.ops_by || "—"}{a.pdd.ops_at ? ` · ${formatDate(a.pdd.ops_at)}` : ""}</span></div>}
          </div>
        )}
        {a.undertaking?.no && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Undertaking Letter</div>
            <div className="text-sm font-semibold text-slate-800">{a.undertaking.no}</div>
            <div className="text-xs text-slate-600">Sent to {(a.undertaking.last_recipients || []).join(", ") || "—"} · Resent {a.undertaking.resend_count || 0} times</div>
            {a.undertaking.document_id && <button onClick={() => viewProof(a.undertaking.document_id)} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1 mt-1"><Eye size={12} /> View PDF</button>}
          </div>
        )}
        {!a.is_current && (
          <div className="text-xs text-slate-500 pt-1 border-t border-slate-100">Reassigned {a.reassigned_at ? formatDate(a.reassigned_at) : "—"} by {a.reassigned_by || "—"}</div>
        )}
      </div>
    </div>
  );
}

export function LeadDetail({ lead: initialLead, lenders, partners, onClose, onChange, docTypes }) {
  const { user } = useAuth();
  const canAssignLender = user?.role === "admin" || user?.role === "sales_manager";
  const perms = user?.permissions || [];
  const canProposal = user?.role === "admin" || perms.includes("generate-proposal");
  const canUndertaking = user?.role === "admin" || perms.includes("generate-undertaking");
  const generateProposal = async () => {
    try {
      const { data } = await api.post(`/leads/${initialLead.id}/proposal/generate`);
      toast.success(`Proposal ${data.proposal_no} generated & saved`);
      // Open the freshly saved PDF from Documents
      try {
        const r = await api.get(`/documents/${data.document_id}`);
        const resp = await fetch(r.data.data);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (_) {}
      loadDocs();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [undOpen, setUndOpen] = useState(false);
  const [undEmails, setUndEmails] = useState("");
  const [undCcSm, setUndCcSm] = useState(true);
  const [undNote, setUndNote] = useState("");
  const [undBusy, setUndBusy] = useState(false);
  const openUndertaking = () => { setUndEmails(""); setUndNote(""); setUndCcSm(true); setUndOpen(true); };
  const submitUndertaking = async () => {
    setUndBusy(true);
    try {
      const emails = (undEmails || "").split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(`/leads/${initialLead.id}/undertaking`, {
        additional_emails: emails, cc_sales_manager: undCcSm, note: undNote,
      });
      toast.success(`${data.resent ? "Resent" : "Generated"} ${data.undertaking_no} · emailed ${data.sent} recipient(s)`);
      if ((data.errors || []).length) toast.error(`Some emails failed: ${data.errors.join("; ")}`);
      setUndOpen(false);
      try {
        const r = await api.get(`/documents/${data.document_id}`);
        const resp = await fetch(r.data.data);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (_) {}
      await loadLead(); loadDocs(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUndBusy(false); }
  };
  const types = docTypes && docTypes.length ? docTypes : DOC_TYPES;
  const [lead, setLead] = useState(initialLead);
  const [docs, setDocs] = useState([]);
  const [newStatus, setNewStatus] = useState(initialLead.status);
  const [disbursedAmount, setDisbursedAmount] = useState(initialLead.loan_amount || "");
  const [sanctionAmount, setSanctionAmount] = useState(initialLead.sanction_amount || "");
  const [irr, setIrr] = useState(initialLead.irr || "");
  const [tenure, setTenure] = useState(initialLead.tenure || "");
  const [bankAppNo, setBankAppNo] = useState(initialLead.bank_application_no || "");
  const [loginFieldDefs, setLoginFieldDefs] = useState([]);
  const [loginFieldVals, setLoginFieldVals] = useState({});
  const [disbNo, setDisbNo] = useState(initialLead.disbursement_no || "");
  const [disbType, setDisbType] = useState(initialLead.disbursement_type || "full");
  const [parts, setParts] = useState([{ no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
  const [fullProof, setFullProof] = useState({ id: initialLead.disbursement_proof_doc_id || "", name: initialLead.disbursement_proof_name || "" });
  const [uploadingFull, setUploadingFull] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  // Disbursed To workflow
  const [disbursedTo, setDisbursedTo] = useState(initialLead.disbursed_to || "");
  const [companyBanks, setCompanyBanks] = useState([]);
  const [masterLenders, setMasterLenders] = useState([]);
  const [afBankId, setAfBankId] = useState(initialLead.arjun_fintech_bank_id || "");
  const [receivedAmount, setReceivedAmount] = useState(initialLead.received_amount || "");
  const [receivedUtr, setReceivedUtr] = useState(initialLead.received_utr || "");
  const [receivedDate, setReceivedDate] = useState(initialLead.received_date || new Date().toISOString().slice(0, 10));
  const [receivedProof, setReceivedProof] = useState({ id: initialLead.received_proof_doc_id || "", name: initialLead.received_proof_name || "" });
  const [uploadingReceivedProof, setUploadingReceivedProof] = useState(false);
  const [masterLenderId, setMasterLenderId] = useState(initialLead.master_lender_id || "");
  const [note, setNote] = useState("");
  const [assignId, setAssignId] = useState(initialLead.partner_id || "");
  const [lenderId, setLenderId] = useState(initialLead.lender_id || "");
  const [docType, setDocType] = useState(types[0] || "Aadhaar");
  const [tab, setTab] = useState("track");
  const [stages, setStages] = useState(STAGES);
  const canOpsDecide = user?.role === "admin" || user?.role === "operations_manager";
  const UND_ALLOWED = ["Approved by Ops", "Under Process", "Approved", "Disbursed", "Sent for PDD Approval", "PDD Confirmed", "Closed"];
  const isAdminUser = user?.role === "admin";
  // Admin can generate undertaking anytime once the lead has moved past Approved by Ops
  const undertakingUnlockedForAdmin = (() => {
    if (!isAdminUser) return false;
    if (!Array.isArray(stages) || !stages.length) return UND_ALLOWED.includes(initialLead.status);
    const oi = stages.indexOf("Approved by Ops");
    const ci = stages.indexOf(initialLead.status);
    return oi >= 0 && ci >= oi && initialLead.status !== "Rejected";
  })();
  const canGenerateUndertaking = undertakingUnlockedForAdmin || UND_ALLOWED.includes(initialLead.status);
  const hasUndertaking = !!initialLead.undertaking?.no;
  const canViewPdd = user?.role === "admin" || user?.role === "operations_manager" || user?.role === "sales_manager" || (user?.permissions || []).includes("lead-pdd");
  const canViewContribution = user?.role === "admin" || user?.role === "finance" || user?.role === "operations_manager";
  const [contribution, setContribution] = useState(null);
  const canResubmitPdd = user?.role === "admin" || user?.role === "sales_manager" || user?.role === "master_dsa";
  const [pddOpen, setPddOpen] = useState(false);
  const [pddLoanType, setPddLoanType] = useState("");
  const [pddActs, setPddActs] = useState([]);
  const [pddTypes, setPddTypes] = useState([]);
  const [pddSaving, setPddSaving] = useState(false);
  const [opsAmt, setOpsAmt] = useState(initialLead.sanction_amount || "");
  const [opsIrr, setOpsIrr] = useState(initialLead.irr || "");
  const [opsTenure, setOpsTenure] = useState(initialLead.tenure || "");
  const [opsNote, setOpsNote] = useState("");
  const [opsReject, setOpsReject] = useState(false);
  const [opsChkDefs, setOpsChkDefs] = useState([]);
  const [opsChkItems, setOpsChkItems] = useState([]);
  const [pddOpsActs, setPddOpsActs] = useState([]);
  const [opsUploadingIdx, setOpsUploadingIdx] = useState(-1);
  const [trackReject, setTrackReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // ----- Final PDD (post-disbursement final checklist) -----
  const canSubmitFinalPdd = user?.role === "admin" || user?.role === "sales_manager";
  const canDecideFinalPdd = user?.role === "admin" || user?.role === "operations_manager";
  const [finalTypes, setFinalTypes] = useState([]);
  const [finalPddOpen, setFinalPddOpen] = useState(false);
  const [finalPddLoanType, setFinalPddLoanType] = useState("");
  const [finalPddActs, setFinalPddActs] = useState([]);
  const [finalPddSaving, setFinalPddSaving] = useState(false);
  const [finalRejectOpen, setFinalRejectOpen] = useState(false);
  const [finalRejectNote, setFinalRejectNote] = useState("");
  const [finalApproveOpen, setFinalApproveOpen] = useState(false);
  const [finalOpsActs, setFinalOpsActs] = useState([]);
  const [finalApproveNote, setFinalApproveNote] = useState("");

  const loadLead = () => api.get(`/leads/${lead.id}`).then((r) => setLead(r.data));
  const loadDocs = () => api.get("/documents", { params: { entity_type: "lead", entity_id: lead.id } }).then((r) => setDocs(r.data));

  useEffect(() => { loadDocs(); api.get("/meta").then((r) => setStages(r.data.stages || STAGES)).catch(() => {}); api.get("/settings/pdd").then((r) => setPddTypes(r.data.loan_types || [])).catch(() => {}); api.get("/settings/final-pdd").then((r) => setFinalTypes(r.data.loan_types || [])).catch(() => {}); api.get("/settings/login-fields", { params: { loan_type: initialLead.loan_type || "" } }).then((r) => {
    const defs = r.data.fields || [];
    setLoginFieldDefs(defs);
    const existing = initialLead.login_fields || {};
    const init = {};
    defs.forEach((f) => { init[f.key] = existing[f.key] || (f.key === "bank_application_no" ? (initialLead.bank_application_no || "") : ""); });
    setLoginFieldVals(init);
  }).catch(() => {});
  api.get("/company-banks").then((r) => setCompanyBanks(r.data || [])).catch(() => {});
  api.get("/lenders").then((r) => setMasterLenders(r.data || [])).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load ops check items whenever we enter Ops Approval, keyed on the PDD loan type
  useEffect(() => {
    if (lead.status !== "Ops Approval") return;
    const loanType = lead.pdd?.loan_type || lead.loan_type;
    if (!loanType) { setOpsChkDefs([]); setOpsChkItems([]); return; }
    api.get("/settings/ops-check").then((r) => {
      const lt = (r.data.loan_types || []).find((t) => t.name === loanType);
      const defs = (lt?.activities || []);
      setOpsChkDefs(defs);
      setOpsChkItems(defs.map((d) => ({ name: d.name, requires_document: !!d.requires_document, deviation_allowed: !!d.deviation_allowed, remark: "", document_id: "", document_name: "", deviation: false })));
    }).catch(() => { setOpsChkDefs([]); setOpsChkItems([]); });
  }, [lead.status, lead.pdd?.loan_type, lead.loan_type]);

  // Auto-select the next non-Rejected stage as the target — no manual dropdown
  useEffect(() => {
    const ci = stages.indexOf(lead.status);
    if (ci >= 0 && ci < stages.length - 1) {
      for (let i = ci + 1; i < stages.length; i++) {
        if (stages[i] !== "Rejected") { setNewStatus(stages[i]); return; }
      }
    }
    setNewStatus(lead.status);
  }, [lead.status, stages]);

  const openPddModal = () => {
    const draft = lead.pdd_draft;
    const draftLT = draft?.loan_type && pddTypes.some((t) => t.name === draft.loan_type) ? draft.loan_type : "";
    const guess = draftLT || pddTypes.find((t) => t.name === lead.loan_type)?.name || pddTypes[0]?.name || "";
    setPddLoanType(guess);
    const defs = (pddTypes.find((t) => t.name === guess)?.activities || []);
    const draftMap = new Map((draft?.activities || []).map((a) => [a.name, a]));
    setPddActs(defs.map((a) => {
      const d = draftMap.get(a.name);
      return { ...a, deviation: !!d?.deviation, remark: d?.remark || "", document_id: d?.document_id || null, document_name: d?.document_name || "" };
    }));
    setPddOpen(true);
  };
  const onPddLoanTypeChange = (name) => {
    setPddLoanType(name);
    const defs = (pddTypes.find((t) => t.name === name)?.activities || []);
    setPddActs(defs.map((a) => ({ ...a, deviation: false, remark: "", document_id: null, document_name: "" })));
  };
  const updPddAct = (i, patch) => setPddActs((arr) => arr.map((a, ix) => ix === i ? { ...a, ...patch } : a));
  const pddUpload = async (i, ev) => {
    const file = ev.target.files[0]; ev.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/documents", { entity_type: "lead", entity_id: lead.id, doc_type: `PDD - ${pddActs[i].name}`, file_name: file.name, data: reader.result });
        updPddAct(i, { document_id: data.id, document_name: file.name });
        toast.success("Document uploaded");
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const pddPayload = () => ({
    loan_type: pddLoanType,
    activities: pddActs.map((a) => ({ name: a.name, completed: !a.deviation, deviation: !!a.deviation, remark: a.remark, document_id: a.document_id, document_name: a.document_name })),
  });
  const savePddDraft = async () => {
    setPddSaving(true);
    try {
      await api.post(`/leads/${lead.id}/pdd-draft`, pddPayload());
      toast.success("Draft saved");
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPddSaving(false); }
  };
  const submitPdd = async () => {
    for (const a of pddActs) {
      if (a.deviation && !a.deviation_allowed) { toast.error(`Deviation is not allowed for '${a.name}'`); return; }
      if (!a.remark.trim()) { toast.error(`Remark required for '${a.name}'`); return; }
      if (a.requires_document && !a.deviation && !a.document_id) { toast.error(`Upload document for '${a.name}' (or mark deviation)`); return; }
    }
    setPddSaving(true);
    try {
      await api.post(`/leads/${lead.id}/pdd`, pddPayload());
      toast.success("Submitted to Ops for Approval");
      setPddOpen(false);
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPddSaving(false); }
  };
  const opsApprove = async () => {
    // Validate ops check items client-side for UX
    for (const it of opsChkItems) {
      if (!it.ok) { toast.error(`Tick 'OK' for '${it.name}'`); return; }
      if (!(it.remark || "").trim()) { toast.error(`Remark required for '${it.name}'`); return; }
      if (it.requires_document && !it.document_id) { toast.error(`Document required for '${it.name}'`); return; }
    }
    for (const a of pddOpsActs) {
      if (!a.ok) { toast.error(`Tick 'OK' for PDD activity '${a.name}'`); return; }
      if (!(a.remark || "").trim()) { toast.error(`Ops remark required for '${a.name}'`); return; }
    }
    try {
      await api.post(`/leads/${lead.id}/ops-approve`, {
        note: opsNote,
        ops_check_items: opsChkItems.map((x) => ({ name: x.name, completed: !!x.ok, deviation: !!x.deviation, remark: x.remark || "", document_id: x.document_id || null, document_name: x.document_name || "" })),
        pdd_ops_activities: pddOpsActs.map((x) => ({ name: x.name, ok: !!x.ok, remark: x.remark || "" })),
      });
      toast.success("Lead marked Approved by Ops");
      setOpsNote(""); setOpsChkItems([]); setPddOpsActs([]); await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const uploadOpsChkDoc = async (idx, file) => {
    if (!file) return;
    setOpsUploadingIdx(idx);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await api.post("/documents", { entity_type: "lead", entity_id: lead.id, doc_type: "Ops Check", file_name: file.name, data: reader.result });
          setOpsChkItems((arr) => arr.map((x, i) => i === idx ? { ...x, document_id: data.id, document_name: file.name } : x));
          toast.success("Uploaded");
          loadDocs();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setOpsUploadingIdx(-1); }
      };
      reader.readAsDataURL(file);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setOpsUploadingIdx(-1); }
  };
  const opsRejectDo = async () => {
    if (!opsNote.trim()) { toast.error("Rejection reason required"); return; }
    try {
      await api.post(`/leads/${lead.id}/ops-reject`, { note: opsNote });
      toast.success("Lead rejected — moved back to Login Done");
      setOpsReject(false); setOpsNote(""); await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const rejectLead = async () => {
    if (!rejectReason.trim()) { toast.error("Rejection reason required"); return; }
    try {
      await api.patch(`/leads/${lead.id}/status`, { status: "Rejected", note: rejectReason });
      toast.success("Lead marked Rejected");
      setTrackReject(false); setRejectReason(""); await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const restoreLead = async () => {
    try {
      await api.post(`/leads/${lead.id}/admin-restore`);
      toast.success("Lead restored to its pre-reject stage");
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [reassignWarn, setReassignWarn] = useState(false);
  const [reassignAppNo, setReassignAppNo] = useState("");
  const assignLender = async () => {
    if (!lenderId) return;
    // First attempt — no force_reassign; backend returns 409 if a lender is already assigned
    try {
      await api.patch(`/leads/${lead.id}/assign-lender`, { lender_id: lenderId });
      toast.success("Lender assigned");
      await loadLead();
      onChange && onChange();
    } catch (err) {
      if (err.response?.status === 409) {
        // Need explicit confirmation — surface the warning panel
        setReassignAppNo(""); setReassignWarn(true);
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const confirmReassignLender = async () => {
    if (!reassignAppNo.trim()) { toast.error("A new Bank Application No is required to reassign"); return; }
    try {
      await api.patch(`/leads/${lead.id}/assign-lender`, {
        lender_id: lenderId, force_reassign: true, bank_application_no: reassignAppNo.trim(),
      });
      toast.success("Lender reassigned · lead moved to Login Done");
      setReassignWarn(false); setReassignAppNo("");
      await loadLead();
      onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (lead.status === "Ops Approval" && (lead.pdd?.activities || []).length > 0 && pddOpsActs.length !== lead.pdd.activities.length) {
      setPddOpsActs(lead.pdd.activities.map((a) => ({ name: a.name, ok: false, remark: "" })));
    }
  }, [lead.status, lead.pdd?.activities?.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  const viewProof = async (id) => {
    try {
      const r = await api.get(`/documents/${id}`);
      const dataUrl = r.data.data;
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openFinalPddModal = () => {
    const draft = lead.final_pdd_draft;
    // Loan type is inherited from PDD & Ops — no user selection in the modal
    const pddLT = lead.pdd?.loan_type;
    const guess = (pddLT && finalTypes.some((t) => t.name === pddLT) ? pddLT : "")
      || (draft?.loan_type && finalTypes.some((t) => t.name === draft.loan_type) ? draft.loan_type : "")
      || finalTypes.find((t) => t.name === lead.loan_type)?.name
      || pddLT || lead.loan_type || "";
    setFinalPddLoanType(guess);
    const defs = (finalTypes.find((t) => t.name === guess)?.activities || []);
    const draftMap = new Map((draft?.activities || []).map((a) => [a.name, a]));
    setFinalPddActs(defs.map((a) => {
      const d = draftMap.get(a.name);
      return { ...a, completed: !!d?.completed, remark: d?.remark || "", document_id: d?.document_id || null, document_name: d?.document_name || "" };
    }));
    setFinalPddOpen(true);
  };
  const onFinalLoanTypeChange = (name) => {
    setFinalPddLoanType(name);
    const defs = (finalTypes.find((t) => t.name === name)?.activities || []);
    setFinalPddActs(defs.map((a) => ({ ...a, completed: false, remark: "", document_id: null, document_name: "" })));
  };
  const updFinalAct = (i, patch) => setFinalPddActs((arr) => arr.map((a, ix) => ix === i ? { ...a, ...patch } : a));
  const finalPddUpload = async (i, ev) => {
    const file = ev.target.files[0]; ev.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/documents", { entity_type: "lead", entity_id: lead.id, doc_type: `Final PDD - ${finalPddActs[i].name}`, file_name: file.name, data: reader.result });
        updFinalAct(i, { document_id: data.id, document_name: file.name });
        toast.success("Document uploaded");
        loadDocs();
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const finalPddPayload = () => ({
    loan_type: finalPddLoanType,
    activities: finalPddActs.map((a) => ({ name: a.name, completed: !!a.completed, deviation: false, remark: (a.remark || "").trim(), document_id: a.document_id, document_name: a.document_name })),
  });
  const saveFinalDraft = async () => {
    setFinalPddSaving(true);
    try {
      await api.post(`/leads/${lead.id}/final-pdd-draft`, finalPddPayload());
      toast.success("Draft saved");
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFinalPddSaving(false); }
  };
  const submitFinalPdd = async () => {
    for (const a of finalPddActs) {
      if (!a.completed) { toast.error(`Tick '${a.name}' before submitting`); return; }
      if (!(a.remark || "").trim()) { toast.error(`Remark required for '${a.name}'`); return; }
      if (a.requires_document && !a.document_id) { toast.error(`Upload document for '${a.name}'`); return; }
    }
    setFinalPddSaving(true);
    try {
      await api.post(`/leads/${lead.id}/final-pdd`, finalPddPayload());
      toast.success("Sent to Ops for PDD Approval");
      setFinalPddOpen(false);
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFinalPddSaving(false); }
  };
  const openFinalApproveModal = () => {
    const acts = (lead.final_pdd?.activities || []).map((a) => ({ name: a.name, ok: false, remark: "" }));
    setFinalOpsActs(acts);
    setFinalApproveNote("");
    setFinalApproveOpen(true);
  };
  const updFinalOpsAct = (i, patch) => setFinalOpsActs((arr) => arr.map((a, ix) => ix === i ? { ...a, ...patch } : a));
  const approveFinalPdd = async () => {
    for (const a of finalOpsActs) {
      if (!a.ok) { toast.error(`Tick 'OK' for '${a.name}'`); return; }
      if (!(a.remark || "").trim()) { toast.error(`Add Ops remark for '${a.name}'`); return; }
    }
    try {
      await api.post(`/leads/${lead.id}/final-pdd/approve`, { note: finalApproveNote, ops_activities: finalOpsActs });
      toast.success("Final PDD approved → PDD Confirmed");
      setFinalApproveOpen(false);
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const rejectFinalPdd = async () => {
    if (!finalRejectNote.trim()) { toast.error("Rejection reason required"); return; }
    try {
      await api.post(`/leads/${lead.id}/final-pdd/reject`, { note: finalRejectNote });
      toast.success("Final PDD rejected → back to Disbursed");
      setFinalRejectOpen(false); setFinalRejectNote("");
      await loadLead(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadProof = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api.post("/documents", { entity_type: "lead", entity_id: lead.id, doc_type: "Disbursement Proof", file_name: file.name, data: reader.result });
        resolve({ id: r.data.id, name: file.name });
      } catch (err) { reject(err); }
    };
    reader.readAsDataURL(file);
  });

  const onFullProofPick = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFull(true);
    try {
      const { id, name } = await uploadProof(file);
      setFullProof({ id, name });
      loadDocs();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploadingFull(false);
  };

  const onReceivedProofPick = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingReceivedProof(true);
    try {
      const { id, name } = await uploadProof(file);
      setReceivedProof({ id, name });
      loadDocs();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploadingReceivedProof(false);
  };

  const onProofPick = async (idx, e, list, setList) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const { id, name } = await uploadProof(file);
      const next = [...list];
      next[idx] = { ...next[idx], proof_doc_id: id, proof_name: name };
      setList(next);
      loadDocs();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploadingIdx(-1);
  };

  const cleanParts = (list) => {
    const kept = list.filter((p) => p.no.trim() || p.amount || p.proof_doc_id);
    for (const p of kept) {
      if (!p.no.trim()) { toast.error("Each part needs a Disbursement No"); return null; }
      if (!(parseFloat(p.amount) > 0)) { toast.error("Each part needs an amount greater than 0"); return null; }
      if (!p.proof_doc_id) { toast.error("Upload proof of disbursement for each part"); return null; }
    }
    if (!kept.length) { toast.error("Add at least one disbursement part"); return null; }
    return kept.map((p) => ({ no: p.no.trim(), amount: parseFloat(p.amount), proof_doc_id: p.proof_doc_id, proof_name: p.proof_name }));
  };

  const updateStatus = async () => {
    if (newStatus === "Ops Approval" && lead.status !== "Ops Approval") {
      openPddModal();
      return;
    }
    try {
      const payload = { status: newStatus, note };
      if (newStatus === "Disbursed") {
        if (!disbursedTo) { toast.error("Select Disbursed To"); return; }
        payload.disbursed_to = disbursedTo;
        if (disbursedTo === "arjun_fintech") {
          if (!afBankId) { toast.error("Select Arjun Fintech bank account"); return; }
          payload.arjun_fintech_bank_id = afBankId;
        } else if (disbursedTo === "agrowheels") {
          if (!masterLenderId) { toast.error("Select Master Lender"); return; }
          payload.master_lender_id = masterLenderId;
        }
        payload.disbursement_type = disbType;
        if (disbType === "part") {
          const cp = cleanParts(parts);
          if (!cp) return;
          payload.disbursement_parts = cp;
        } else {
          if (!disbNo.trim()) { toast.error("Disbursement No is required"); return; }
          if (!fullProof.id) { toast.error("Upload proof of disbursement"); return; }
          payload.disbursed_amount = parseFloat(disbursedAmount) || 0;
          payload.disbursement_no = disbNo.trim();
          payload.disbursement_proof_doc_id = fullProof.id;
          payload.disbursement_proof_name = fullProof.name;
        }
      }
      if (newStatus === "Login Done") {
        // Dynamic configured Login Done inputs — every required field must be present
        for (const f of loginFieldDefs) {
          if (f.required && !((loginFieldVals[f.key] || "").toString().trim())) {
            toast.error(`${f.label} is required for Login Done`);
            return;
          }
        }
        const lf = {};
        Object.entries(loginFieldVals).forEach(([k, v]) => {
          const sv = (v ?? "").toString().trim();
          if (sv) lf[k] = sv;
        });
        payload.login_fields = lf;
        if (lf.bank_application_no) payload.bank_application_no = lf.bank_application_no;
        else if (bankAppNo.trim()) payload.bank_application_no = bankAppNo.trim();
      }
      if (newStatus === "Approved") {
        if (!(parseFloat(sanctionAmount) > 0)) { toast.error("Sanction Amount is required to mark Approved"); return; }
        if (!(parseFloat(irr) > 0)) { toast.error("IRR % is required to mark Approved"); return; }
        if (!(parseInt(tenure) > 0)) { toast.error("Tenure (months) is required to mark Approved"); return; }
        payload.sanction_amount = parseFloat(sanctionAmount);
        payload.irr = parseFloat(irr);
        payload.tenure = parseInt(tenure);
      }
      await api.patch(`/leads/${lead.id}/status`, payload);
      toast.success(`Status updated to ${newStatus}`);
      setNote("");
      setParts([{ no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
      await loadLead();
      onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addMoreParts = async () => {
    const cp = cleanParts(parts);
    if (!cp) return;
    try {
      await api.patch(`/leads/${lead.id}/disbursement-parts`, { parts: cp });
      toast.success("Disbursement part(s) added");
      setParts([{ no: "", amount: "", proof_doc_id: "", proof_name: "" }]);
      await loadLead();
      onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const assign = async () => {
    if (!assignId) return;
    try {
      await api.patch(`/leads/${lead.id}/assign`, { partner_id: assignId });
      toast.success("Lead reassigned");
      await loadLead();
      onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/documents", { entity_type: "lead", entity_id: lead.id, doc_type: docType, file_name: file.name, data: reader.result });
        toast.success("Document uploaded");
        loadDocs();
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const delDoc = async (id) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Document removed");
    loadDocs();
  };

  return (
    <Modal open={true} onClose={onClose} title={`${lead.customer_name} · ${lead.loan_type}`} size="lg" testId="lead-detail-modal">
      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-100 flex-wrap">
        <StatusBadge status={lead.status} />
        <div className="text-sm text-slate-500">Customer: <span className="font-medium text-slate-800" data-testid="lead-customer-name">{lead.customer_name}</span></div>
        <div className="text-sm text-slate-500">Phone: <span className="font-medium text-slate-800">{lead.customer_phone || "—"}</span></div>
        {lead.customer_email && <div className="text-sm text-slate-500" data-testid="lead-customer-email">Email: <span className="font-medium text-slate-800">{lead.customer_email}</span></div>}
        <div className="text-sm text-slate-500">Amount: <span className="font-mono font-semibold text-slate-800">{formatINR(lead.loan_amount)}</span></div>
        <div className="text-sm text-slate-500">Lender: <span className="font-medium text-slate-800">{lead.lender_name || "—"}</span></div>
        <div className="text-sm text-slate-500">Partner: <span className="font-medium text-slate-800">{lead.partner_name || "Unassigned"}</span></div>
        <div className="text-sm text-slate-500">Aadhaar: <span className="font-medium text-slate-800">{lead.aadhaar || "—"}</span></div>
        <div className="text-sm text-slate-500">PAN: <span className="font-medium text-slate-800">{lead.pan || "—"}</span></div>
        {(lead.address || lead.city || lead.tehsil || lead.pincode) && (
          <div className="w-full text-sm text-slate-500" data-testid="lead-address">Address: <span className="font-medium text-slate-800">{[lead.address, lead.tehsil, lead.city, lead.pincode].filter(Boolean).join(", ") || "—"}</span></div>
        )}
        {lead.bank_application_no && <div className="text-sm text-slate-500">Bank App No: <span className="font-medium text-slate-800">{lead.bank_application_no}</span></div>}
        {lead.sanction_amount > 0 && <div className="text-sm text-slate-500">Sanction: <span className="font-mono font-semibold text-slate-800">{formatINR(lead.sanction_amount)}</span></div>}
        {lead.irr > 0 && <div className="text-sm text-slate-500" data-testid="lead-irr">IRR: <span className="font-medium text-slate-800">{lead.irr}%</span></div>}
        {lead.tenure > 0 && <div className="text-sm text-slate-500" data-testid="lead-tenure">Tenure: <span className="font-medium text-slate-800">{lead.tenure} months</span></div>}
        {lead.disbursement_no && <div className="text-sm text-slate-500">Disb. No: <span className="font-medium text-slate-800">{lead.disbursement_no}</span></div>}
        {lead.disbursement_type === "full" && lead.disbursement_proof_doc_id && <button data-testid="view-full-proof" onClick={() => viewProof(lead.disbursement_proof_doc_id)} className="text-sm text-emerald-600 hover:underline">Disb. Proof</button>}
        {(canProposal || canUndertaking) && (
          <div className="ml-auto flex items-center gap-2">
            {canProposal && (() => {
              const existingProp = (docs || []).find((d) => d.doc_type === "Proposal");
              const label = existingProp?.sn ? `Regenerate Proposal · ${existingProp.sn}` : "Generate Proposal";
              return (
                <button data-testid="generate-proposal-button" onClick={generateProposal} title={existingProp?.sn ? `Regenerating will overwrite ${existingProp.sn}` : "Generate a new proposal PDF"} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold ${existingProp?.sn ? "bg-indigo-600 hover:bg-indigo-500" : "bg-slate-900 hover:bg-slate-800"}`}><FileText size={14} /> {label}</button>
              );
            })()}
            {canUndertaking && (
            <button
              data-testid="generate-undertaking-button"
              onClick={openUndertaking}
              disabled={!(lead.status && (isAdminUser ? (stages.indexOf("Approved by Ops") >= 0 && stages.indexOf(lead.status) >= stages.indexOf("Approved by Ops") && lead.status !== "Rejected") : UND_ALLOWED.includes(lead.status)))}
              title={UND_ALLOWED.includes(lead.status) || (isAdminUser && stages.indexOf(lead.status) >= stages.indexOf("Approved by Ops") && lead.status !== "Rejected") ? (lead.undertaking?.no ? `Existing: ${lead.undertaking.no}` : "Generate undertaking letter") : "Available once the lead crosses Approved by Ops"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold ${(UND_ALLOWED.includes(lead.status) || (isAdminUser && stages.indexOf(lead.status) >= stages.indexOf("Approved by Ops") && lead.status !== "Rejected")) ? (lead.undertaking?.no ? "bg-indigo-600 hover:bg-indigo-500" : "bg-emerald-600 hover:bg-emerald-500") : "bg-slate-300 cursor-not-allowed"}`}>
              <ScrollText size={14} /> {lead.undertaking?.no ? `Resend Undertaking${lead.undertaking?.no ? ` · ${lead.undertaking.no}` : ""}` : "Generate Undertaking"}
            </button>
            )}
          </div>
        )}
      </div>

      {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="lead-custom-fields">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{lead.loan_type} Details</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            {Object.entries(lead.custom_fields).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm"><span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium text-slate-800">{String(v || "—")}</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-5 border-b border-slate-100">
        {[["track", "Track Status"], ["assign", "Assignment"], ["docs", "Documents"], ...(canViewPdd && (lead.pdd || (lead.pdd_history || []).length > 0) ? [["pdd", "PDD & Ops"]] : []), ...(["Disbursed", "Sent for PDD Approval", "PDD Confirmed"].includes(lead.status) ? [["final-pdd", "Final PDD"]] : []), ...(((lead.lender_attempts || []).length > 0) ? [["lenders", "Lender Attempts"]] : []), ...(canViewContribution ? [["contribution", "Contribution"]] : []), ["history", "History"]].map(([k, label]) => (
          <button key={k} data-testid={`lead-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-emerald-500 text-emerald-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "track" && (
        <div className="space-y-4">
          {lead.status === "Ops Approval" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="ops-decision-panel">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-amber-900">Pending Ops Approval</div>
                  <div className="text-xs text-amber-700">PDD submitted{lead.pdd?.loan_type ? ` for ${lead.pdd.loan_type}` : ""} by {lead.pdd?.submitted_by || "—"}. {canOpsDecide ? "You can approve or reject below." : "Only Operations Manager can decide."}</div>
                </div>
              </div>
              {(lead.pdd?.activities || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-white overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 text-[10px] uppercase tracking-wider text-amber-700">
                      <tr>
                        <th className="text-left px-3 py-2">Activity</th>
                        <th className="text-left px-3 py-2">Sales Remark</th>
                        <th className="text-left px-3 py-2">Document</th>
                        {canOpsDecide && !opsReject && <th className="text-left px-3 py-2 w-12">OK</th>}
                        {canOpsDecide && !opsReject && <th className="text-left px-3 py-2">Ops Remark *</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {lead.pdd.activities.map((a, i) => {
                        const oa = pddOpsActs[i] || {};
                        return (
                          <tr key={i} className="border-t border-amber-100 align-top" data-testid={`ops-pdd-activity-${i}`}>
                            <td className="px-3 py-2 font-medium text-slate-800">{a.name}{a.deviation && <div className="text-[10px] text-amber-700 font-semibold">Deviation</div>}</td>
                            <td className="px-3 py-2 text-slate-600">{a.remark || "—"}</td>
                            <td className="px-3 py-2">{a.document_id ? <button data-testid={`ops-view-pdd-doc-${i}`} onClick={() => viewProof(a.document_id)} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1"><Eye size={12} /> {a.document_name || "View"}</button> : <span className="text-slate-400 text-xs">—</span>}</td>
                            {canOpsDecide && !opsReject && <td className="px-3 py-2"><input data-testid={`pdd-ops-ok-${i}`} type="checkbox" checked={!!oa.ok} onChange={(e) => setPddOpsActs((arr) => arr.map((x, j) => j === i ? { ...x, ok: e.target.checked } : x))} className="w-4 h-4 accent-emerald-600" /></td>}
                            {canOpsDecide && !opsReject && <td className="px-3 py-2"><input data-testid={`pdd-ops-remark-${i}`} value={oa.remark || ""} onChange={(e) => setPddOpsActs((arr) => arr.map((x, j) => j === i ? { ...x, remark: e.target.value } : x))} placeholder="Ops remark *" className={`${inputCls} py-1.5 text-xs`} /></td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {canOpsDecide && (
                <>
                  {opsChkDefs.length > 0 && !opsReject && (
                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="ops-check-section">
                      <div className="bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Ops Checklist — tick OK on every item to unlock approval</div>
                      <div className="divide-y divide-slate-100">
                        {opsChkItems.map((it, i) => (
                          <div key={i} className="p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`ops-check-row-${i}`}>
                            <label className="sm:col-span-1 flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer" title="Mark OK">
                              <input data-testid={`ops-check-ok-${i}`} type="checkbox" checked={!!it.ok} onChange={(e) => setOpsChkItems((arr) => arr.map((x, j) => j === i ? { ...x, ok: e.target.checked } : x))} className="w-4 h-4 accent-emerald-600" />
                              <span className="text-[11px] font-semibold text-slate-600">OK</span>
                            </label>
                            <div className="sm:col-span-4 text-sm font-medium text-slate-800">{it.name} {it.requires_document && <span className="text-rose-500">*doc</span>}</div>
                            <input data-testid={`ops-check-remark-${i}`} value={it.remark} onChange={(e) => setOpsChkItems((arr) => arr.map((x, j) => j === i ? { ...x, remark: e.target.value } : x))} className={`${inputCls} sm:col-span-4`} placeholder="Remark *" />
                            <div className="sm:col-span-3 flex items-center gap-1.5">
                              {it.document_id ? (
                                <button type="button" onClick={() => viewProof(it.document_id)} className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1"><Eye size={12} /> {it.document_name || "View"}</button>
                              ) : (
                                <label className="text-xs px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer inline-flex items-center gap-1">
                                  <FileUp size={12} /> {opsUploadingIdx === i ? "Uploading…" : (it.requires_document ? "Upload *" : "Upload")}
                                  <input data-testid={`ops-check-upload-${i}`} type="file" className="hidden" onChange={(e) => uploadOpsChkDoc(i, e.target.files[0])} />
                                </label>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className={labelCls}>{opsReject ? "Reason for Rejection *" : "Approval Note"}</label>
                    <input data-testid="ops-note" value={opsNote} onChange={(e) => setOpsNote(e.target.value)} className={inputCls} placeholder={opsReject ? "Why is this lead being rejected?" : "Optional remark"} />
                  </div>
                  <div className="flex justify-end gap-2">
                    {opsReject ? (
                      <>
                        <button onClick={() => { setOpsReject(false); setOpsNote(""); }} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
                        <button data-testid="ops-reject-confirm" onClick={opsRejectDo} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold">Confirm Reject → Login Done</button>
                      </>
                    ) : (
                      <>
                        <button data-testid="ops-reject-open" onClick={() => setOpsReject(true)} className="px-4 py-2 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700 text-sm font-semibold">Reject</button>
                        <button data-testid="ops-approve-confirm" onClick={opsApprove} disabled={(opsChkDefs.length > 0 && !opsChkItems.every((x) => x.ok)) || (pddOpsActs.length > 0 && !pddOpsActs.every((x) => x.ok))} className={`${btnPrimary} ${((opsChkDefs.length > 0 && !opsChkItems.every((x) => x.ok)) || (pddOpsActs.length > 0 && !pddOpsActs.every((x) => x.ok))) ? "opacity-50 cursor-not-allowed" : ""}`} title="Tick OK on every PDD activity and Ops Check item to unlock approval">Ops Approve → Approved by Ops</button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {lead.status !== "Ops Approval" && (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {newStatus === "Disbursed" && lead.status !== "Disbursed" && (
              <>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Disburse by Lender to *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["arjun_fintech", "Arjun Fintech"], ["agrowheels", "Agrowheels"], ["customer", "Customer"]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`disbursed-to-${v}`} onClick={() => setDisbursedTo(v)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${disbursedTo === v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {disbursedTo === "arjun_fintech" && (
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Arjun Fintech Bank *</label>
                    <select data-testid="arjun-fintech-bank-select" value={afBankId} onChange={(e) => setAfBankId(e.target.value)} className={inputCls}>
                      <option value="">— Select bank —</option>
                      {companyBanks.map((b) => (
                        <option key={b.id} value={b.id}>{b.bank_name} · A/C {b.account_no}</option>
                      ))}
                    </select>
                  </div>
                )}
                {disbursedTo === "agrowheels" && (
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Master Lender *</label>
                    <select data-testid="master-lender-select" value={masterLenderId} onChange={(e) => setMasterLenderId(e.target.value)} className={inputCls}>
                      <option value="">— Select lender —</option>
                      {masterLenders.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label className={labelCls}>Disbursement Type</label>
                  <div className="flex gap-3">
                    {[["full", "Full"], ["part", "Part"]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`disb-type-${v}`} onClick={() => setDisbType(v)}
                        className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${disbType === v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {disbType === "full" ? (
                  <>
                    <div>
                      <label className={labelCls}>Disbursed Amount (₹)</label>
                      <input data-testid="disbursed-amount-input" type="number" value={disbursedAmount} onChange={(e) => setDisbursedAmount(e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Disbursement No *</label>
                      <input data-testid="disbursement-no-input" value={disbNo} onChange={(e) => setDisbNo(e.target.value)} className={inputCls} placeholder="Disbursal reference no" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Proof of Disbursement *</label>
                      <label className={`${btnSecondary} cursor-pointer`} data-testid="full-proof-label">
                        <FileUp size={15} /> {uploadingFull ? "Uploading…" : (fullProof.name ? "Replace" : "Upload")}
                        <input type="file" className="hidden" data-testid="full-proof-input" onChange={onFullProofPick} />
                      </label>
                      {fullProof.name && <span className="text-xs text-emerald-600 ml-3" data-testid="full-proof-name">{fullProof.name}</span>}
                    </div>
                  </>
                ) : (
                  <PartsEditor parts={parts} setParts={setParts} uploadingIdx={uploadingIdx} onProofPick={onProofPick} />
                )}
              </>
            )}
            {newStatus === "Login Done" && (
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(loginFieldDefs.length > 0 ? loginFieldDefs : [{ key: "bank_application_no", label: "Bank Application No", required: true, locked: true }]).map((f) => (
                  <div key={f.key}>
                    <label className={labelCls}>{f.label}{f.required ? " *" : ""}</label>
                    <input
                      data-testid={`login-field-input-${f.key}`}
                      value={loginFieldVals[f.key] || ""}
                      onChange={(e) => {
                        setLoginFieldVals((v) => ({ ...v, [f.key]: e.target.value }));
                        if (f.key === "bank_application_no") setBankAppNo(e.target.value);
                      }}
                      className={inputCls}
                      placeholder={f.key === "bank_application_no" ? "Lender's application / reference no" : f.label}
                    />
                  </div>
                ))}
              </div>
            )}
            {newStatus === "Approved" && (
              <>
                <div>
                  <label className={labelCls}>Sanction Amount (₹) *</label>
                  <input data-testid="sanction-amount-input" type="number" value={sanctionAmount} onChange={(e) => setSanctionAmount(e.target.value)} className={inputCls} placeholder="Amount sanctioned by the lender" />
                </div>
                <div>
                  <label className={labelCls}>IRR % *</label>
                  <input data-testid="irr-input" type="number" step="0.01" value={irr} onChange={(e) => setIrr(e.target.value)} className={inputCls} placeholder="e.g. 14.5" />
                </div>
                <div>
                  <label className={labelCls}>Tenure (months) *</label>
                  <input data-testid="tenure-input" type="number" value={tenure} onChange={(e) => setTenure(e.target.value)} className={inputCls} placeholder="e.g. 36" />
                </div>
              </>
            )}
          </div>
          <div>
            <label className={labelCls}>Note (optional)</label>
            <input data-testid="status-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Add a remark for this update…" />
          </div>
          {lead.status === "Closed" ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="closed-lock-banner">
              This lead is <span className="font-semibold text-slate-800">Closed</span> — no further status changes or rejection are allowed.
            </div>
          ) : lead.status === "Sent for PDD Approval" ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="sent-pdd-lock-banner">
              This lead is <span className="font-semibold text-amber-900">Sent for PDD Approval</span> — Ops Manager will approve or reject on the <span className="font-semibold">Final PDD</span> tab. Manual status changes are locked.
            </div>
          ) : (
          <div className="flex justify-end gap-2">
            {lead.status === "Rejected" && isAdminUser && (
              <button data-testid="restore-lead-button" onClick={restoreLead} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold" title={`Restore to ${lead.status_before_reject || "Login Done"}`}>
                Restore Lead → {lead.status_before_reject || "Login Done"}
              </button>
            )}
            {!["Rejected", "Disbursed", "Closed", "Sent for PDD Approval"].includes(lead.status) && (
              <button data-testid="track-reject-open" onClick={() => setTrackReject(true)} className="px-4 py-2 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700 text-sm font-semibold">Reject Lead</button>
            )}
            {lead.status !== "Rejected" && (() => {
              const goingLogin = newStatus === "Login Done" && lead.status !== "Login Done";
              const missing = goingLogin && loginFieldDefs.some((f) => f.required && !((loginFieldVals[f.key] || "").toString().trim()));
              return (
                <button data-testid="update-status-button" onClick={updateStatus} disabled={missing} title={missing ? "Fill every required Login Done input to proceed" : undefined} className={`${btnPrimary} ${missing ? "opacity-50 cursor-not-allowed" : ""}`}>{newStatus && newStatus !== lead.status ? `Move to ${newStatus}` : "Update Note"} <ArrowRight size={16} /></button>
              );
            })()}
          </div>
          )}
          {trackReject && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-3" data-testid="track-reject-panel">
              <div className="text-sm font-semibold text-rose-900">Reject Lead</div>
              <div>
                <label className={labelCls}>Reason for Rejection *</label>
                <input data-testid="track-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className={inputCls} placeholder="Why is this lead being rejected?" autoFocus />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setTrackReject(false); setRejectReason(""); }} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
                <button data-testid="track-reject-confirm" onClick={rejectLead} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold">Confirm Reject → Rejected</button>
              </div>
            </div>
          )}
          {newStatus === "Disbursed" && lead.status !== "Disbursed" && <p className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">Marking as Disbursed will auto-calculate the commission payout for the assigned partner.</p>}
          </>)}

          {lead.status === "Disbursed" && lead.disbursement_type === "part" && (
            <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="disbursed-parts-section">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">Disbursement Parts ({(lead.disbursement_parts || []).length})</div>
                <div className="text-sm text-slate-600">Disbursed: <span className="font-mono font-semibold text-slate-900">{formatINR(lead.disbursed_amount)}</span></div>
              </div>
              <div className="space-y-2">
                {(lead.disbursement_parts || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50" data-testid={`disbursed-part-${i}`}>
                    <div>
                      <div className="text-sm font-medium text-slate-800">Part {i + 1} · {p.no}</div>
                      <div className="text-xs text-slate-400">{formatDate(p.at)} · {p.by}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-semibold text-slate-800">{formatINR(p.amount)}</span>
                      {p.proof_doc_id && <button data-testid={`view-proof-${i}`} onClick={() => viewProof(p.proof_doc_id)} className="text-xs text-emerald-600 hover:underline">Proof</button>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-sm font-semibold text-slate-800 pt-1">Add more parts</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PartsEditor parts={parts} setParts={setParts} uploadingIdx={uploadingIdx} onProofPick={onProofPick} />
              </div>
              <div className="flex justify-end">
                <button data-testid="add-more-parts-button" onClick={addMoreParts} className={btnPrimary}><Plus size={16} /> Add Part(s)</button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "assign" && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Assign to Partner / DSA</label>
            <select data-testid="assign-partner-select" value={assignId} onChange={(e) => setAssignId(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.partner_type})</option>)}
            </select>
          </div>
          <div className="flex justify-end">
            <button data-testid="assign-lead-button" onClick={assign} className={btnPrimary}>Reassign Lead</button>
          </div>
          {canAssignLender && (
            <div className="border-t border-slate-100 pt-4">
              {lead.lender_id && !reassignWarn ? (
                <>
                  <label className={labelCls}>Assigned Lender</label>
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="assigned-lender-card">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{lead.lender_name}</div>
                      <div className="text-[11px] text-slate-500">Bank App No: {lead.bank_application_no || "—"}</div>
                    </div>
                    <button data-testid="reassign-lender-open" onClick={() => { setLenderId(""); setReassignAppNo(""); setReassignWarn(true); }} className="px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold">Reassign Lender</button>
                  </div>
                </>
              ) : reassignWarn ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="reassign-lender-panel">
                  <div>
                    <div className="text-sm font-semibold text-amber-900">Reassign Lender — Warning</div>
                    <div className="text-xs text-amber-700 mt-0.5">Reassigning will move this lead back to <b>Login Done</b>. Sanction / IRR / Tenure / PDD / Undertaking data on this lead will be cleared, and a new Bank Application No is mandatory before the lead can progress again.</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>New Lender *</label>
                      <select data-testid="reassign-lender-select" value={lenderId} onChange={(e) => setLenderId(e.target.value)} className={inputCls}>
                        <option value="">Select lender…</option>
                        {(lenders || []).filter((l) => l.id !== lead.lender_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>New Bank Application No *</label>
                      <input data-testid="reassign-app-no" value={reassignAppNo} onChange={(e) => setReassignAppNo(e.target.value)} className={inputCls} placeholder="Fresh reference from the new lender" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setReassignWarn(false); setReassignAppNo(""); setLenderId(lead.lender_id || ""); }} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
                    <button data-testid="reassign-lender-confirm" onClick={confirmReassignLender} disabled={!lenderId || !reassignAppNo.trim()} className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold">Confirm Reassign → Login Done</button>
                  </div>
                </div>
              ) : (
                <>
                  <label className={labelCls}>Assign Lender</label>
                  <select data-testid="assign-lender-select" value={lenderId} onChange={(e) => setLenderId(e.target.value)} className={inputCls}>
                    <option value="">Select lender…</option>
                    {(lenders || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  <div className="flex justify-end mt-3">
                    <button data-testid="assign-lender-button" onClick={assignLender} className={btnPrimary}>Assign Lender</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "docs" && (
        <div className="space-y-4">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className={labelCls}>Document Type</label>
              <select data-testid="doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
                {types.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <label className={`${btnSecondary} cursor-pointer`} data-testid="doc-upload-label">
              <FileUp size={16} /> Upload
              <input data-testid="doc-upload-input" type="file" className="hidden" onChange={upload} />
            </label>
          </div>
          <div className="space-y-2">
            {docs.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No documents uploaded yet.</div>}
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50">
                <div>
                  <div className="text-sm font-medium text-slate-800">{d.file_name}</div>
                  <div className="text-xs text-slate-400">{d.doc_type} · {formatDate(d.uploaded_at)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`view-doc-${d.id}`} onClick={() => viewProof(d.id)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50" title="View document"><Eye size={15} /></button>
                  {user?.role === "admin" && (
                    <button data-testid={`delete-doc-${d.id}`} onClick={() => delDoc(d.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50" title="Delete document"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "pdd" && canViewPdd && (
        <div className="space-y-4" data-testid="lead-pdd-tab">
          {lead.pdd ? (
            <div className={`rounded-xl border p-4 space-y-3 ${lead.pdd.ops_decision === "approved" ? "border-emerald-200 bg-emerald-50" : lead.pdd.ops_decision === "rejected" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid="pdd-current-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current PDD Submission</div>
                  <div className="text-sm font-semibold text-slate-800 mt-0.5">{lead.pdd.loan_type}</div>
                  <div className="text-xs text-slate-500">Submitted by <span className="font-medium text-slate-700">{lead.pdd.submitted_by || "—"}</span> · {lead.pdd.submitted_at ? formatDate(lead.pdd.submitted_at) : "—"}</div>
                </div>
                <div className="text-right">
                  {lead.pdd.ops_decision === "approved" && <span className="inline-block px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-semibold">Approved by Ops</span>}
                  {lead.pdd.ops_decision === "rejected" && <span className="inline-block px-2 py-1 rounded-md bg-rose-100 text-rose-800 text-xs font-semibold">Rejected by Ops</span>}
                  {!lead.pdd.ops_decision && <span className="inline-block px-2 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold">Awaiting Ops</span>}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Activity</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Remark</th>
                      <th className="text-left px-3 py-2">Document</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lead.pdd.activities || []).map((a, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`pdd-activity-${i}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{a.name}</td>
                        <td className="px-3 py-2">{a.deviation ? <span className="text-amber-700 font-medium">Deviation</span> : <span className="text-emerald-700">Completed</span>}</td>
                        <td className="px-3 py-2 text-slate-600">{a.remark || "—"}</td>
                        <td className="px-3 py-2">{a.document_id ? <button data-testid={`view-pdd-doc-${i}`} onClick={() => viewProof(a.document_id)} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1"><Eye size={12} /> {a.document_name || "View"}</button> : <span className="text-slate-400 text-xs">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {lead.pdd.ops_decision && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Ops Manager Remark</div>
                  <div className="text-sm text-slate-800">{lead.pdd.ops_note || "—"}</div>
                  <div className="text-xs text-slate-500 mt-1">{lead.pdd.ops_by || "—"} · {lead.pdd.ops_at ? formatDate(lead.pdd.ops_at) : ""}</div>
                </div>
              )}
              {lead.pdd.ops_decision === "approved" && (lead.pdd.ops_check_items || []).length > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-white overflow-hidden" data-testid="ops-check-verified">
                  <div className="bg-emerald-50 px-3 py-2 text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">Ops Checklist verified before approval</div>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Remark</th><th className="text-left px-3 py-2">Document</th></tr>
                    </thead>
                    <tbody>
                      {lead.pdd.ops_check_items.map((it, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`ops-check-verified-row-${i}`}>
                          <td className="px-3 py-2 font-medium text-slate-800">{it.name}</td>
                          <td className="px-3 py-2 text-slate-600">{it.remark || "—"}</td>
                          <td className="px-3 py-2">{it.document_id ? <button onClick={() => viewProof(it.document_id)} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1"><Eye size={12} /> {it.document_name || "View"}</button> : <span className="text-slate-400 text-xs">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {lead.status === "Login Done" && lead.pdd.ops_decision === "rejected" && canResubmitPdd && (
                <div className="flex justify-end">
                  <button data-testid="pdd-resubmit-button" onClick={openPddModal} className={btnPrimary}>Edit &amp; Resubmit PDD</button>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500" data-testid="pdd-empty">No PDD submitted yet.</div>
          )}

          {(lead.pdd_history || []).length > 0 && (
            <div className="space-y-2" data-testid="pdd-history-list">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 pt-1">Earlier PDD Submissions</div>
              {(lead.pdd_history || []).slice().reverse().map((h, idx) => (
                <details key={idx} className="rounded-lg border border-slate-200 bg-white" data-testid={`pdd-history-${idx}`}>
                  <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-sm">
                    <div>
                      <span className="font-medium text-slate-800">{h.loan_type}</span>
                      <span className="text-xs text-slate-500 ml-2">by {h.submitted_by || "—"} · {h.submitted_at ? formatDate(h.submitted_at) : ""}</span>
                    </div>
                    {h.ops_decision === "approved" && <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">Approved</span>}
                    {h.ops_decision === "rejected" && <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold">Rejected</span>}
                    {!h.ops_decision && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">No decision</span>}
                  </summary>
                  <div className="border-t border-slate-100 p-3 space-y-2">
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                          <tr><th className="text-left px-3 py-2">Activity</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Remark</th><th className="text-left px-3 py-2">Document</th></tr>
                        </thead>
                        <tbody>
                          {(h.activities || []).map((a, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-3 py-2 font-medium text-slate-800">{a.name}</td>
                              <td className="px-3 py-2">{a.deviation ? <span className="text-amber-700">Deviation</span> : <span className="text-emerald-700">Completed</span>}</td>
                              <td className="px-3 py-2 text-slate-600">{a.remark || "—"}</td>
                              <td className="px-3 py-2">{a.document_id ? <button onClick={() => viewProof(a.document_id)} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1"><Eye size={12} /> {a.document_name || "View"}</button> : <span className="text-slate-400 text-xs">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {h.ops_decision && (
                      <div className="text-xs text-slate-600"><span className="font-semibold">Ops Remark:</span> {h.ops_note || "—"} <span className="text-slate-400">· {h.ops_by || "—"}{h.ops_at ? ` · ${formatDate(h.ops_at)}` : ""}</span></div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "lenders" && (
        <LenderAttemptsTab lead={lead} viewProof={viewProof} formatDate={formatDate} />
      )}

      {tab === "contribution" && canViewContribution && (
        <ContributionTab lead={lead} contribution={contribution} setContribution={setContribution} />
      )}

      {tab === "final-pdd" && (
        <div className="space-y-4" data-testid="lead-final-pdd-tab">
          {lead.status === "Disbursed" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-amber-900">Final PDD pending</div>
                <div className="text-xs text-amber-700 mt-0.5">Tick every item and upload the required documents, then send to Ops for PDD Approval.</div>
              </div>
              {canSubmitFinalPdd && finalTypes.length > 0 && (
                <button data-testid="open-final-pdd" onClick={openFinalPddModal} className={btnPrimary}>Update PDD Status</button>
              )}
              {canSubmitFinalPdd && finalTypes.length === 0 && (
                <div className="text-xs text-amber-700">Configure Settings → Final PDD Update first.</div>
              )}
            </div>
          )}

          {(lead.status === "Sent for PDD Approval" || lead.status === "PDD Confirmed" || (lead.status === "Disbursed" && lead.final_pdd?.ops_decision === "rejected")) && lead.final_pdd && (
            <div className={`rounded-xl border p-4 space-y-3 ${lead.final_pdd.ops_decision === "approved" ? "border-emerald-200 bg-emerald-50" : lead.final_pdd.ops_decision === "rejected" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid="final-pdd-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Final PDD Submission</div>
                  <div className="text-sm font-semibold text-slate-800 mt-0.5">{lead.final_pdd.loan_type}</div>
                  <div className="text-xs text-slate-500">Submitted by <span className="font-medium text-slate-700">{lead.final_pdd.submitted_by || "—"}</span> · {lead.final_pdd.submitted_at ? formatDate(lead.final_pdd.submitted_at) : "—"}</div>
                </div>
                <div className="text-right">
                  {lead.final_pdd.ops_decision === "approved" && <span className="inline-block px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-semibold">PDD Confirmed</span>}
                  {lead.final_pdd.ops_decision === "rejected" && <span className="inline-block px-2 py-1 rounded-md bg-rose-100 text-rose-800 text-xs font-semibold">Rejected by Ops</span>}
                  {!lead.final_pdd.ops_decision && <span className="inline-block px-2 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold">Awaiting Ops</span>}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Sales Remark</th><th className="text-left px-3 py-2">Document</th><th className="text-left px-3 py-2">Ops OK</th><th className="text-left px-3 py-2">Ops Remark</th></tr>
                  </thead>
                  <tbody>
                    {(lead.final_pdd.activities || []).map((a, i) => {
                      const oa = ((lead.final_pdd.ops_activities || []).find((x) => x.name === a.name)) || {};
                      return (
                        <tr key={i} className="border-t border-slate-100 align-top" data-testid={`final-pdd-row-${i}`}>
                          <td className="px-3 py-2 font-medium text-slate-800">{a.name}</td>
                          <td className="px-3 py-2 text-slate-600">{a.remark || "—"}</td>
                          <td className="px-3 py-2">{a.document_id ? <button onClick={() => viewProof(a.document_id)} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1"><Eye size={12} /> {a.document_name || "View"}</button> : <span className="text-slate-400 text-xs">—</span>}</td>
                          <td className="px-3 py-2">{oa.ok ? <span className="text-emerald-700">✓</span> : <span className="text-slate-400">—</span>}</td>
                          <td className="px-3 py-2 text-slate-600">{oa.remark || <span className="text-slate-400">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {lead.final_pdd.ops_decision && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Ops Manager Remark</div>
                  <div className="text-sm text-slate-800">{lead.final_pdd.ops_note || "—"}</div>
                  <div className="text-xs text-slate-500 mt-1">{lead.final_pdd.ops_by || "—"} · {lead.final_pdd.ops_at ? formatDate(lead.final_pdd.ops_at) : ""}</div>
                </div>
              )}
              {lead.status === "Sent for PDD Approval" && canDecideFinalPdd && (
                <div className="flex justify-end gap-2 pt-1">
                  {finalRejectOpen ? (
                    <div className="flex-1 space-y-2">
                      <label className={labelCls}>Rejection reason *</label>
                      <textarea data-testid="final-reject-note" value={finalRejectNote} onChange={(e) => setFinalRejectNote(e.target.value)} rows={2} className={inputCls} placeholder="Why is the Final PDD being rejected? (lead reverts to Disbursed)" />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => { setFinalRejectOpen(false); setFinalRejectNote(""); }} className={btnSecondary}>Cancel</button>
                        <button data-testid="final-reject-confirm" onClick={rejectFinalPdd} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold">Reject PDD → back to Disbursed</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button data-testid="final-reject-open" onClick={() => setFinalRejectOpen(true)} className="px-4 py-2 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700 text-sm font-semibold">Reject PDD</button>
                      <button data-testid="final-approve-open" onClick={openFinalApproveModal} className={btnPrimary}>Review &amp; Approve</button>
                    </>
                  )}
                </div>
              )}
              {lead.status === "Disbursed" && lead.final_pdd?.ops_decision === "rejected" && canSubmitFinalPdd && (
                <div className="flex justify-end pt-1">
                  <button data-testid="final-resubmit" onClick={openFinalPddModal} className={btnPrimary}>Update &amp; Resubmit</button>
                </div>
              )}
            </div>
          )}

          {!lead.final_pdd && lead.status !== "Disbursed" && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Final PDD has not been captured yet for this lead.</div>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-0">
          {(lead.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="flex gap-3 pb-4 relative">
              <div className="flex flex-col items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5" />
                {i < lead.history.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
              </div>
              <div className="pb-2">
                <div className="text-sm font-medium text-slate-800">{h.status}</div>
                {h.note && <div className="text-xs text-slate-500">{h.note}</div>}
                <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Clock size={11} /> {formatDate(h.at)} · {h.by}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={finalApproveOpen} onClose={() => setFinalApproveOpen(false)} title="Approve Final PDD — Ops Review" testId="final-pdd-approve-modal">
        <div className="space-y-4">
          <div className="text-xs text-slate-500">Confirm each submitted item with an <span className="font-semibold text-emerald-700">OK</span> tick and Ops remark. Approval unlocks only when every item is signed off.</div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2 w-14">OK</th>
                  <th className="text-left px-3 py-2">Ops Remark *</th>
                  <th className="text-left px-3 py-2">Sales Note</th>
                </tr>
              </thead>
              <tbody>
                {finalOpsActs.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400 text-xs">Submission has no items.</td></tr>}
                {finalOpsActs.map((a, i) => {
                  const src = (lead.final_pdd?.activities || []).find((x) => x.name === a.name) || {};
                  return (
                    <tr key={i} className="border-t border-slate-100 align-top" data-testid={`final-approve-row-${i}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{a.name}{src.document_id && <button onClick={() => viewProof(src.document_id)} className="ml-2 text-[10px] text-emerald-600 hover:underline inline-flex items-center gap-1"><Eye size={11} />{src.document_name || "doc"}</button>}</td>
                      <td className="px-3 py-2"><input data-testid={`final-approve-ok-${i}`} type="checkbox" checked={!!a.ok} onChange={(e) => updFinalOpsAct(i, { ok: e.target.checked })} className="w-4 h-4 accent-emerald-600" /></td>
                      <td className="px-3 py-2"><input data-testid={`final-approve-remark-${i}`} value={a.remark || ""} onChange={(e) => updFinalOpsAct(i, { remark: e.target.value })} placeholder="Verified / Note from Ops" className={`${inputCls} py-1.5 text-xs`} /></td>
                      <td className="px-3 py-2 text-[11px] text-slate-500">{src.remark || <span className="text-slate-300">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div>
            <label className={labelCls}>Ops overall note (optional)</label>
            <textarea data-testid="final-approve-note" value={finalApproveNote} onChange={(e) => setFinalApproveNote(e.target.value)} rows={2} className={inputCls} placeholder="Add an overall approval comment" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setFinalApproveOpen(false)} className={btnSecondary}>Cancel</button>
            <button data-testid="final-approve-confirm" onClick={approveFinalPdd} className={btnPrimary}>Approve → PDD Confirmed</button>
          </div>
        </div>
      </Modal>

      <Modal open={finalPddOpen} onClose={() => setFinalPddOpen(false)} title="Final PDD Update — Move to Ops for Approval" testId="final-pdd-modal">
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Loan Type</div>
              <div className="text-sm font-semibold text-slate-800" data-testid="final-pdd-loan-type-value">{finalPddLoanType || "—"}</div>
            </div>
            <div className="text-[10px] text-slate-400">from PDD &amp; Ops</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-3 py-2 w-14">Done</th><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Remark *</th><th className="text-left px-3 py-2">Document</th></tr>
              </thead>
              <tbody>
                {finalPddActs.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400 text-xs">No items configured for this loan type.</td></tr>}
                {finalPddActs.map((a, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`final-pdd-modal-row-${i}`}>
                    <td className="px-3 py-2 align-top"><input data-testid={`final-pdd-check-${i}`} type="checkbox" checked={!!a.completed} onChange={(e) => updFinalAct(i, { completed: e.target.checked })} className="w-4 h-4 accent-emerald-600" /></td>
                    <td className="px-3 py-2 align-top"><div className="font-medium text-slate-800">{a.name}</div>{a.requires_document && <div className="text-[10px] text-amber-700 font-semibold">DOC REQUIRED</div>}</td>
                    <td className="px-3 py-2 align-top"><input data-testid={`final-pdd-remark-${i}`} value={a.remark || ""} onChange={(e) => updFinalAct(i, { remark: e.target.value })} placeholder="Verified / Note" className={`${inputCls} py-1.5 text-xs`} /></td>
                    <td className="px-3 py-2 align-top">
                      {a.requires_document ? (
                        a.document_id ? (
                          <div className="flex items-center gap-2 text-xs">
                            <button onClick={() => viewProof(a.document_id)} className="text-emerald-600 hover:underline inline-flex items-center gap-1"><Eye size={12} /> {a.document_name || "View"}</button>
                            <label className="cursor-pointer text-slate-500 hover:text-slate-800">Replace<input data-testid={`final-pdd-upload-${i}`} type="file" className="hidden" onChange={(e) => finalPddUpload(i, e)} /></label>
                          </div>
                        ) : (
                          <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline"><FileUp size={12} /> Upload<input data-testid={`final-pdd-upload-${i}`} type="file" className="hidden" onChange={(e) => finalPddUpload(i, e)} /></label>
                        )
                      ) : (
                        <span className="text-[10px] text-slate-400">Not required</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setFinalPddOpen(false)} className={btnSecondary}>Cancel</button>
            <button data-testid="final-pdd-save-draft" onClick={saveFinalDraft} disabled={finalPddSaving || finalPddActs.length === 0} className={btnSecondary}>Save Draft</button>
            <button data-testid="final-pdd-submit" onClick={submitFinalPdd} disabled={finalPddSaving || finalPddActs.length === 0} className={btnPrimary}>Move to Ops for PDD Approval</button>
          </div>
        </div>
      </Modal>

      <Modal open={pddOpen} onClose={() => setPddOpen(false)} title="PDD Checklist — Submit to Ops" testId="pdd-checklist-modal">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Loan Type *</label>
            <select data-testid="pdd-loan-type" value={pddLoanType} onChange={(e) => onPddLoanTypeChange(e.target.value)} className={inputCls}>
              {pddTypes.length === 0 && <option value="">— No PDD loan types configured —</option>}
              {pddTypes.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          {pddActs.length === 0 && pddLoanType && <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">This loan type has no activities configured. Configure them in Settings → PDD Checklist.</p>}
          {pddActs.map((a, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`pdd-modal-activity-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-800">{a.name}{a.requires_document ? " · document required" : ""}</div>
                {a.deviation_allowed ? (
                  <label className="inline-flex items-center gap-1.5 text-sm"><input data-testid={`pdd-act-deviation-${i}`} type="checkbox" checked={a.deviation} onChange={(e) => updPddAct(i, { deviation: e.target.checked })} className="w-4 h-4 accent-amber-600" /> Deviation Required</label>
                ) : (
                  <span className="text-[11px] text-slate-400 italic">Deviation not allowed</span>
                )}
              </div>
              <input data-testid={`pdd-act-remark-${i}`} value={a.remark} onChange={(e) => updPddAct(i, { remark: e.target.value })} className={inputCls} placeholder={a.deviation ? "Reason for deviation *" : "Remark *"} />
              {a.requires_document && !a.deviation && (
                <div className="flex items-center gap-2">
                  <label className={`${btnSecondary} cursor-pointer`} data-testid={`pdd-act-upload-${i}`}><FileUp size={14} /> {a.document_id ? "Replace" : "Upload"}<input type="file" className="hidden" onChange={(e) => pddUpload(i, e)} /></label>
                  {a.document_name && <span className="text-xs text-emerald-600 truncate">{a.document_name}</span>}
                </div>
              )}
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setPddOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="pdd-save-draft" onClick={savePddDraft} disabled={pddSaving || pddActs.length === 0} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold">Save Draft</button>
            <button data-testid="pdd-submit" onClick={submitPdd} disabled={pddSaving || pddActs.length === 0} className={btnPrimary}>Submit to Ops for Approval</button>
          </div>
        </div>
      </Modal>

      <Modal open={undOpen} onClose={() => setUndOpen(false)} title={lead.undertaking?.no ? `Resend Undertaking · ${lead.undertaking.no}` : "Generate Lender Undertaking"} testId="undertaking-modal">
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
            <div>Lender: <b className="text-slate-800">{lead.lender_name || "—"}</b></div>
            <div>Customer: <b className="text-slate-800">{lead.customer_name}</b> · Bank App No: {lead.bank_application_no || "—"}</div>
            {lead.undertaking?.no ? (
              <div className="mt-1 text-[11px] text-slate-500">Reusing existing letter <b>{lead.undertaking.no}</b> · last sent {lead.undertaking.last_sent_at ? formatDate(lead.undertaking.last_sent_at) : "—"} to {(lead.undertaking.last_recipients || []).join(", ") || "—"}. No new sequence number will be issued.</div>
            ) : (
              <div className="mt-1 text-[11px] text-slate-500">The PDF will be auto-numbered (UND-XXXX), saved as an attachment on this lead, and emailed to the lender's email on file (assign via Lenders page). Sales Manager is CC'd by default.</div>
            )}
          </div>
          <div>
            <label className={labelCls}>Additional Emails (optional)</label>
            <input data-testid="und-additional-emails" value={undEmails} onChange={(e) => setUndEmails(e.target.value)} className={inputCls} placeholder="comma or space separated e.g. ops@lender.com, credit@lender.com" />
          </div>
          <div>
            <label className={labelCls}>Note on Letter (optional)</label>
            <textarea data-testid="und-note" value={undNote} onChange={(e) => setUndNote(e.target.value)} rows={3} className={inputCls} placeholder="Any specific note to include in the undertaking…" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="und-cc-sm" type="checkbox" checked={undCcSm} onChange={(e) => setUndCcSm(e.target.checked)} className="w-4 h-4 accent-emerald-600" /> CC Sales Manager ({lead.sales_manager_name || "—"})</label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setUndOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="und-submit" onClick={submitUndertaking} disabled={undBusy} className={btnPrimary}>{undBusy ? (lead.undertaking?.no ? "Resending…" : "Generating…") : (lead.undertaking?.no ? "Resend to Recipients" : "Generate & Send")}</button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}
